import React from 'react'
import { IMasterCurricula } from 'infrastructure/api'
import { byStringFieldSort, openTab } from '../../Helpers'
import { IUpdateInfo, migrateVersions } from '../../../providers/mutations'
import { Box, Button, MenuItem, Select } from '@mui/material'
import { useGetVersionMapping, IParsedVersionMapping } from '../TreeActions'
import { useMastersForProduct } from '../../../api/tree.hooks'

export function MigrateSchools({ productId }: { productId: number }) {
  const getVersionMapping = useGetVersionMapping()
  const masterVersions = useMastersForProduct(productId)
  const [toVersion, setToVersion] = React.useState('')
  const [fromVersion, setFromVersion] = React.useState('')
  const [sortedMasters, setSortedMasters] = React.useState<IMasterCurricula[]>(
    [],
  )
  const [valid, setValid] = React.useState(false)
  const [migrating, setMigrating] = React.useState(false)
  const [message, setMessage] = React.useState('')
  const [mappingMapping, setMappingMapping] = React.useState<{
    [k: string]: IParsedVersionMapping
  }>({})

  React.useEffect(() => {
    if (!masterVersions) return
    const sorted = masterVersions.sort(byStringFieldSort('createdAt')).reverse()
    setToVersion(sorted[0]?.name)
    setFromVersion(sorted[1]?.name)
    setSortedMasters(sorted)
  }, [masterVersions])

  React.useEffect(() => {
    if (!fromVersion || !toVersion) return

    const fromTree = sortedMasters.find((m) => m.name === fromVersion)
    const toTree = sortedMasters.find((m) => m.name === toVersion)

    if (!fromTree || !toTree) return

    const mappingKey = `${fromVersion}:${toVersion}`
    if (mappingMapping[mappingKey]) return

    getVersionMapping({
      fromTree: { tree_id: fromTree.treeId, version: fromTree.version },
      toTree: { tree_id: toTree.treeId, version: toTree.version },
    }).then((resp) => {
      if (!resp) return
      setMappingMapping((prev) => ({ ...prev, [mappingKey]: resp }))
    })
  }, [toVersion, fromVersion, sortedMasters, mappingMapping])

  React.useEffect(() => {
    if (!masterVersions) return
    setMessage('')
    const toTree = masterVersions.find((t) => t.name === toVersion)
    const fromTree = masterVersions.find((t) => t.name === fromVersion)
    if (!toTree || !fromTree) {
      setValid(false)
      return
    }
    const comp = toTree.createdAt.localeCompare(fromTree.createdAt)
    if (comp < 0) {
      setMessage('Migrations can go only forward, not backwards')
      setValid(false)
      return
    }

    const mappingKey = `${fromVersion}:${toVersion}`
    const mapping = mappingMapping[mappingKey]
    if (!mapping?.matched.length) {
      setMessage('Mapping missing for these versions')
      setValid(true)
      return
    }

    if (mapping.missing.length) {
      setMessage(
        'There are missing mappings between these versions, complete mapping before migrating',
      )
    }
    setValid(!mapping.missing.length)
  }, [toVersion, fromVersion, masterVersions, mappingMapping])

  function versions() {
    return sortedMasters.map((m) => m.name)
  }

  function goToSelectedMapping() {
    if (!masterVersions || !fromVersion || !toVersion) return
    const toTree = masterVersions.find((t) => t.name === toVersion)
    const fromTree = masterVersions.find((t) => t.name === fromVersion)
    if (!fromTree || !toTree) return
    const prevIndex = sortedMasters.indexOf(fromTree)
    const toIndex = sortedMasters.indexOf(toTree)
    goToMapping(sortedMasters, prevIndex, toIndex)
  }

  function doMigration() {
    if (!masterVersions) return
    const toTree = masterVersions.find((t) => t.name === toVersion)
    const fromTree = masterVersions.find((t) => t.name === fromVersion)
    if (!toTree || !fromTree || !valid) return
    setMigrating(true)
    const updateInfo: IUpdateInfo = {
      destinationVersion: {
        treeId: toTree.treeId,
        version: toTree.version,
        name: toTree.name,
        title: toTree.title,
      },
      versionToUpdate: {
        name: fromTree.name,
        version: fromTree.version,
      },
    }
    migrateVersions([updateInfo])
      .then(() => {
        setMessage('Request sent')
      })
      .catch(() => {
        setMessage('Request error')
      })
      .finally(() => {
        setMigrating(false)
      })
  }

  return (
    <Box>
      <Box padding={'10px'}>
        Migrate To Version
        <VersionSelect
          selected={toVersion}
          versions={versions()}
          onSelect={setToVersion}
        />
      </Box>
      <Box padding={'10px'}>
        From Version
        <VersionSelect
          selected={fromVersion}
          versions={versions()}
          onSelect={setFromVersion}
        />
      </Box>
      <Box>
        <Button disabled={!valid || migrating} onClick={doMigration}>
          Do It
        </Button>
        <Button onClick={() => setMappingMapping({})}>R</Button>
      </Box>
      <Box>
        <Button onClick={goToSelectedMapping}>Go To Mapping</Button>
      </Box>
      {migrating && <Box>Please wait a few and refresh to see changes</Box>}
      {message}
    </Box>
  )
}

function VersionSelect({
  selected,
  versions,
  onSelect,
}: {
  selected: string
  versions: string[]
  onSelect: (v: string) => void
}) {
  return (
    <Box>
      <Select<string>
        variant={'outlined'}
        value={selected}
        onChange={(e) => onSelect(e.target.value)}
      >
        {versions.map((v) => (
          <MenuItem key={v} value={v}>
            {v}
          </MenuItem>
        ))}
      </Select>
    </Box>
  )
}

function goToMapping(
  versions: IMasterCurricula[],
  prevIndex: number = 1,
  toIndex: number = 0,
) {
  const prev = versions[prevIndex]
  if (!prev) return
  const mostRecent = versions[toIndex]
  openTab(
    `/version_mapping/from/${prev.treeId}:${prev.version}/to/${mostRecent.treeId}:${mostRecent.version}?rf=mismatchedPaths,mismatchedTitles`,
  )
}
